/**
 * WALL-E Workbench — VS Code-style Theme Engine
 * 
 * Each theme defines custom Fluent 2 token overrides that get merged
 * into the FluentProvider, giving us full control over the entire
 * visual surface from a single config object.
 */
import type { Theme } from '@fluentui/react-components';
import { webDarkTheme, webLightTheme } from '@fluentui/react-components';

export interface WalleTheme {
  id: string;
  name: string;
  base: 'dark' | 'light';
  preview: string; // Short gradient CSS for the theme picker preview swatch
  overrides: Partial<Theme>;
}

// ─────────────────────────────────────────────────────
// Theme Definitions
// ─────────────────────────────────────────────────────

export const THEMES: WalleTheme[] = [
  {
    id: 'fluent-dark',
    name: 'Fluent Dark',
    base: 'dark',
    preview: 'linear-gradient(135deg, #1b1b1f, #2d2d30)',
    overrides: {}, // Pure Fluent defaults
  },
  {
    id: 'fluent-light',
    name: 'Fluent Light',
    base: 'light',
    preview: 'linear-gradient(135deg, #ffffff, #f5f5f5)',
    overrides: {},
  },
  {
    id: 'tokyo-night',
    name: 'Tokyo Night',
    base: 'dark',
    preview: 'linear-gradient(135deg, #1a1b26, #24283b)',
    overrides: {
      colorNeutralBackground1: '#1a1b26',
      colorNeutralBackground2: '#16161e',
      colorNeutralBackground3: '#24283b',
      colorNeutralForeground1: '#c0caf5',
      colorNeutralForeground2: '#a9b1d6',
      colorNeutralForeground3: '#565f89',
      colorBrandBackground: '#7aa2f7',
      colorBrandForeground1: '#7aa2f7',
      colorBrandStroke1: '#7aa2f7',
      colorNeutralStroke1: '#292e42',
      colorNeutralStroke2: '#1e2030',
      colorPaletteRedForeground1: '#f7768e',
      colorPaletteGreenForeground1: '#9ece6a',
      colorPaletteYellowForeground1: '#e0af68',
    },
  },
  {
    id: 'tokyo-night-storm',
    name: 'Tokyo Night Storm',
    base: 'dark',
    preview: 'linear-gradient(135deg, #24283b, #414868)',
    overrides: {
      colorNeutralBackground1: '#24283b',
      colorNeutralBackground2: '#1f2335',
      colorNeutralBackground3: '#292e42',
      colorNeutralForeground1: '#c0caf5',
      colorNeutralForeground2: '#a9b1d6',
      colorNeutralForeground3: '#565f89',
      colorBrandBackground: '#7dcfff',
      colorBrandForeground1: '#7dcfff',
      colorBrandStroke1: '#7dcfff',
      colorNeutralStroke1: '#3b4261',
      colorNeutralStroke2: '#292e42',
      colorPaletteRedForeground1: '#f7768e',
      colorPaletteGreenForeground1: '#73daca',
      colorPaletteYellowForeground1: '#ff9e64',
    },
  },
  {
    id: 'synthwave-84',
    name: "SynthWave '84",
    base: 'dark',
    preview: 'linear-gradient(135deg, #262335, #2a2139)',
    overrides: {
      colorNeutralBackground1: '#262335',
      colorNeutralBackground2: '#1e1a2e',
      colorNeutralBackground3: '#2a2139',
      colorNeutralForeground1: '#ffffff',
      colorNeutralForeground2: '#bbbbbb',
      colorNeutralForeground3: '#848bbd',
      colorBrandBackground: '#ff7edb',
      colorBrandForeground1: '#ff7edb',
      colorBrandStroke1: '#ff7edb',
      colorNeutralStroke1: '#3b3455',
      colorNeutralStroke2: '#2a2139',
      colorPaletteRedForeground1: '#fe4450',
      colorPaletteGreenForeground1: '#72f1b8',
      colorPaletteYellowForeground1: '#fede5d',
    },
  },
  {
    id: 'monokai',
    name: 'Monokai',
    base: 'dark',
    preview: 'linear-gradient(135deg, #272822, #3e3d32)',
    overrides: {
      colorNeutralBackground1: '#272822',
      colorNeutralBackground2: '#1e1f1c',
      colorNeutralBackground3: '#3e3d32',
      colorNeutralForeground1: '#f8f8f2',
      colorNeutralForeground2: '#cfcfc2',
      colorNeutralForeground3: '#75715e',
      colorBrandBackground: '#66d9ef',
      colorBrandForeground1: '#66d9ef',
      colorBrandStroke1: '#66d9ef',
      colorNeutralStroke1: '#3e3d32',
      colorNeutralStroke2: '#272822',
      colorPaletteRedForeground1: '#f92672',
      colorPaletteGreenForeground1: '#a6e22e',
      colorPaletteYellowForeground1: '#e6db74',
    },
  },
  {
    id: 'solarized-dark',
    name: 'Solarized Dark',
    base: 'dark',
    preview: 'linear-gradient(135deg, #002b36, #073642)',
    overrides: {
      colorNeutralBackground1: '#002b36',
      colorNeutralBackground2: '#00212b',
      colorNeutralBackground3: '#073642',
      colorNeutralForeground1: '#839496',
      colorNeutralForeground2: '#93a1a1',
      colorNeutralForeground3: '#586e75',
      colorBrandBackground: '#268bd2',
      colorBrandForeground1: '#268bd2',
      colorBrandStroke1: '#268bd2',
      colorNeutralStroke1: '#073642',
      colorNeutralStroke2: '#002b36',
      colorPaletteRedForeground1: '#dc322f',
      colorPaletteGreenForeground1: '#859900',
      colorPaletteYellowForeground1: '#b58900',
    },
  },
  {
    id: 'night-sky',
    name: 'Night Sky',
    base: 'dark',
    preview: 'linear-gradient(135deg, #0d1117, #161b22)',
    overrides: {
      colorNeutralBackground1: '#0d1117',
      colorNeutralBackground2: '#010409',
      colorNeutralBackground3: '#161b22',
      colorNeutralForeground1: '#e6edf3',
      colorNeutralForeground2: '#c9d1d9',
      colorNeutralForeground3: '#484f58',
      colorBrandBackground: '#58a6ff',
      colorBrandForeground1: '#58a6ff',
      colorBrandStroke1: '#58a6ff',
      colorNeutralStroke1: '#21262d',
      colorNeutralStroke2: '#161b22',
      colorPaletteRedForeground1: '#f85149',
      colorPaletteGreenForeground1: '#3fb950',
      colorPaletteYellowForeground1: '#d29922',
    },
  },
  {
    id: 'nord',
    name: 'Nord',
    base: 'dark',
    preview: 'linear-gradient(135deg, #2e3440, #3b4252)',
    overrides: {
      colorNeutralBackground1: '#2e3440',
      colorNeutralBackground2: '#272c36',
      colorNeutralBackground3: '#3b4252',
      colorNeutralForeground1: '#eceff4',
      colorNeutralForeground2: '#d8dee9',
      colorNeutralForeground3: '#4c566a',
      colorBrandBackground: '#88c0d0',
      colorBrandForeground1: '#88c0d0',
      colorBrandStroke1: '#88c0d0',
      colorNeutralStroke1: '#434c5e',
      colorNeutralStroke2: '#3b4252',
      colorPaletteRedForeground1: '#bf616a',
      colorPaletteGreenForeground1: '#a3be8c',
      colorPaletteYellowForeground1: '#ebcb8b',
    },
  },
];

/**
 * Resolves a WalleTheme into a full Fluent Theme object
 * by merging overrides onto the appropriate base theme.
 */
export function resolveTheme(themeId: string): Theme {
  const walleTh = THEMES.find(t => t.id === themeId);
  if (!walleTh) return webLightTheme;
  
  const base = walleTh.base === 'light' ? webLightTheme : webDarkTheme;
  return { ...base, ...walleTh.overrides } as Theme;
}

export function getThemeById(themeId: string): WalleTheme | undefined {
  return THEMES.find(t => t.id === themeId);
}
